import { useMemo } from 'react';
// 🛰️ Updated import to point to the unified shared types
import { AppState, CalendarEvent as EngineEvent, CustomCalendar, DataScope } from '@/types';
import { parseISO, areIntervalsOverlapping, isValid } from 'date-fns';

/**
 * 🛰️ MASTER CALENDAR STATE HOOK
 * Refactored for Hybrid Scoping: Merges Personal Partition and Household Hub data.
 * Adheres to Google Data Privacy standards via strict ownership filtering.
 */

export const useCalendarState = (
  personalEvents: EngineEvent[],
  householdEvents: EngineEvent[],
  customCalendarDefinitions: CustomCalendar[], // 🎯 Renamed for clarity: these are definitions, not events
  activeCalendarIds: string[],
  currentUserId: string,
  currentHouseholdId: string | null,
  activeMembers: string[],
  activeScope: DataScope
) => {

  // Helper to ensure valid Date objects for date-fns math
  const toDate = (dateInput: any): Date => {
    if (dateInput instanceof Date) return dateInput;
    return typeof dateInput === 'string' ? parseISO(dateInput) : new Date(dateInput);
  };

  // --- 1. SECURE DATA MERGE & HYBRID FILTERING ---
  const mergedEvents = useMemo(() => {
    const allRawEngineEvents = [...personalEvents, ...householdEvents]; // Combine all potential EngineEvents

    // Filter events based on active calendar IDs and scope
    const filteredEvents = allRawEngineEvents.filter((e: EngineEvent) => { // Use EngineEvent consistently
      const isPersonalCalendarActive = activeCalendarIds.includes('personal');
      const isHouseholdCalendarActive = activeCalendarIds.includes('household');
      const isCustomCalendarEvent = e.customCalendarId && activeCalendarIds.includes(e.customCalendarId);

      let shouldInclude = false;

      // Logic for PERSONAL scope
      if (activeScope === 'PERSONAL') {
        // Include personal events if personal calendar is active and current user is owner
        if (e.source === 'personal' && e.ownerId === currentUserId && isPersonalCalendarActive) {
          shouldInclude = true;
        }
      }
      // Logic for SHARED (Household) scope
      else if (activeScope === 'SHARED') {
        // Include household events if household calendar is active and a household is selected
        if (e.source === 'household' && currentHouseholdId && isHouseholdCalendarActive) {
          shouldInclude = true;
        }
      }
      // Logic for BOTH scope
      else if (activeScope === 'BOTH') {
        // Include personal events if personal calendar is active and current user is owner
        if (e.source === 'personal' && e.ownerId === currentUserId && isPersonalCalendarActive) {
          shouldInclude = true;
        }
        // Include household events if household calendar is active and a household is selected
        if (e.source === 'household' && currentHouseholdId && isHouseholdCalendarActive) {
          shouldInclude = true;
        }
      }

      // If an event is part of an active custom calendar, it should also be included,
      // regardless of the main 'personal'/'household' filters above, if it's owned by the user
      // or if it's a household event within an active household. This part may need refinement
      // based on exact custom calendar visibility rules.
      if (isCustomCalendarEvent && (e.ownerId === currentUserId || (e.source === 'household' && currentHouseholdId))) {
         shouldInclude = true;
      }


      return shouldInclude;
    });

    // --- 🧬 CHRONOLOGICAL UNIFICATION ---
    return filteredEvents.sort((a, b) => {
      const timeA = toDate(a.start).getTime();
      const timeB = toDate(b.start).getTime();
      return timeA - timeB;
    });
  }, [
    personalEvents,
    householdEvents,
    activeCalendarIds,
    currentUserId,
    currentHouseholdId,
    activeScope,
  ]);
  // --- 2. CONFLICT RADAR LOGIC ---
  const hasConflicts = useMemo(() => {
    // Privacy Logic: Conflicts are only alerted for the current user's perspective
    // to prevent leaking data about other members' private schedules.
    const radarEvents = mergedEvents.filter(e =>
      e.ownerId === currentUserId || (e.source === 'household' && currentHouseholdId) // Consider household events only if a household is active
    );

    for (let i = 0; i < radarEvents.length; i++) {
      for (let j = i + 1; j < radarEvents.length; j++) {
        const startA = toDate(radarEvents[i].start);
        const endA = toDate(radarEvents[i].end);
        const startB = toDate(radarEvents[j].start);
        const endB = toDate(radarEvents[j].end);

        if (isValid(startA) && isValid(endA) && isValid(startB) && isValid(endB)) {
          if (areIntervalsOverlapping({ start: startA, end: endA }, { start: startB, end: endB })) {
            return true;
          }
        }
      }
    }
    return false;
  }, [mergedEvents, currentUserId, currentHouseholdId]);

  // --- 3. MEMBER PERSPECTIVE GROUPING ---
  const eventsByMember = useMemo(() => {
    const groups: Record<string, EngineEvent[]> = {}; // Use EngineEvent[] consistently

    // If no household, only the current user column exists
    const membersToProcess = currentHouseholdId ? activeMembers : [currentUserId];

    (membersToProcess || []).forEach((mId: string) => {
      groups[mId] = mergedEvents.filter((event: EngineEvent) => { // Use EngineEvent consistently
        // Shared events visible to everyone in the grid if a household is active
        if (event.source === 'household' && currentHouseholdId) return true;
        // Personal events are visible only in their owner's column
        return event.ownerId === mId;
      });
    });

    return groups;
  }, [mergedEvents, activeMembers, currentUserId, currentHouseholdId]);

  return {
    events: mergedEvents,
    eventsByMember,
    hasConflicts,
    totalVisibleCount: mergedEvents.length,
    activeScope // Returning scope for UI context awareness
  };
};